import { ConsultationsService } from "@publicPortal/services/ConsultationsService";
import React, { useEffect, useState } from "react";
import Box from "@mui/material/Box";
import Comments from "@publicPortal/components/comments/Comments";
import type { ICommentCard } from "@publicPortal/components/comments/commentCard/CommentCard";
import { Tab, Tabs, TextField } from "@mui/material";
import "../PlanningApplication.css";
import { GetPublicConsulteeCommentsResponse, GetUserConsulteeCommentsResponse, IConsulteeCommentToSave } from "@shared/publicportal/contracts/ConsulteeComments";
import CommentOpinionSelector, { OpinionTypes, OPINIONS } from "./components/CommentOpinionSelector";
import InputErrorMessage from "@mirage/components/inputErrorMessage/InputErrorMessage";
import { CommentsTabProps } from "./PACommentsTabContent";
import NoCommentsMessage from "./components/NoCommentsMessage";
import DateFormatter from "@shared/mirage/helpers/DateFormatter";
import NewCommentAttachments, { commentFiles } from "./components/NewCommentAttachments";
import { useRecoilValue } from "recoil";
import useAlertService from "@mirage/hooks/useAlertService";
import { PublicConsulteeTokenDetails } from "@shared/mirage/core/contracts/User";
import { PublicPortalEntities } from "@shared/mirage/contracts/PublicPortalEntities";
import UserCustomProperty from "@shared/mirage/utils/getUserCustomProperty";
import { validatePublicCommentAttachments } from "@shared/mirage/helpers/ValidatePublicCommentAttachments";
import NewComment from "@publicPortal/components/comments/newComment/NewComment";

const ConsultationsTabTypes = {
    USER_COMMENTS: "user-consultations",
    PUBLIC_COMMENTS: "public-consultations",
} as const;
type ConsultationsTabTypes = typeof ConsultationsTabTypes[keyof typeof ConsultationsTabTypes];

const PAConsultationsTabContent: React.FC<CommentsTabProps> = ({ paId, newCommentsDisabledReason, user }) => {
    const consultationsService = new ConsultationsService();
    const [commentOpinion, setCommentOpinion] = useState<OpinionTypes>(OPINIONS.consultee.Neutral);
    const [publicComments, setPublicComments] = useState<GetPublicConsulteeCommentsResponse>({} as GetPublicConsulteeCommentsResponse);
    const [userComments, setUserComments] = useState<GetUserConsulteeCommentsResponse>({} as GetUserConsulteeCommentsResponse);
    const [remainAnonymous, setRemainAnonymous] = useState("no");
    const [newCommentContent, setNewCommentContent] = useState({ value: "", error: "", attachmentError: "" });
    const [currentTab, setCurrentTab] = useState<ConsultationsTabTypes>(ConsultationsTabTypes.PUBLIC_COMMENTS);
    const commentAttachments = useRecoilValue<File[]>(commentFiles);
    const showAlert = useAlertService();
    const consulteeId = UserCustomProperty.getUserCustomPropertyValue(user, "consulteeId");
    const consulteeTokenString = localStorage.getItem("verifyConsultee.token");
    const consulteeToken: PublicConsulteeTokenDetails = consulteeTokenString && JSON.parse(consulteeTokenString);
    const showUserConsultations = currentTab === "user-consultations";

    useEffect(() => {
        getPublicConsulteeComments();
        getUserConsulteeComments();
    }, []);

    return (
        <Box className="comments__comments-container">
            {!user?.id ? null : (
                <Tabs
                    className="comments__comments-tabs"
                    aria-label="Consultations Navigation Tabs"
                    variant="scrollable"
                    scrollButtons="auto"
                    value={currentTab}
                    onChange={(event, newTab: ConsultationsTabTypes) => setCurrentTab(newTab)}
                >
                    <Tab
                        disabled={!user?.id}
                        className="comments__comments-tabs_sub-tab"
                        key={"my-consultations"}
                        disableRipple
                        label={"My Consultations"}
                        value={ConsultationsTabTypes.USER_COMMENTS}
                    />
                    <Tab
                        className="comments__comments-tabs_sub-tab"
                        key={"public-consultations"}
                        disableRipple
                        label={"Public Consultations"}
                        value={ConsultationsTabTypes.PUBLIC_COMMENTS}
                    />
                </Tabs>
            )}
            {showUserConsultations ? renderMyConsultations() : renderPublicConsultations()}
        </Box>
    );

    function renderMyConsultations() {
        if (!consulteeId) {
            return <NoCommentsMessage message="Only consultee user can add consultations." variant="disabled" />;
        }
        return (
            <>
                {!newCommentsDisabledReason ? getNewCommentContent() : <NoCommentsMessage message={newCommentsDisabledReason} variant="disabled" />}
                <Comments className="comments__my-comments" records={getUserConsultationCommentCards()} />
            </>
        );
    }

    function renderPublicConsultations() {
        if (publicComments?.comments?.length) {
            return <Comments records={getConsultationCommentCards()} />;
        }
        return <NoCommentsMessage message="There are no consultations yet." variant="empty" />;
    }

    function getNewCommentContent() {
        return (
            <NewComment handleSave={handleNewCommentSave} label="Add Consultation">
                <CommentOpinionSelector type="consultee" opinion={commentOpinion} changeOpinion={(newOpinion) => setCommentOpinion(newOpinion)} />
                <TextField
                    error={!!newCommentContent.error}
                    helperText={<InputErrorMessage passed={!newCommentContent.error} message={newCommentContent.error} />}
                    multiline
                    minRows={4}
                    label="Your Comment"
                    value={newCommentContent.value}
                    onChange={(e) => handleContentChange(e.target.value)}
                />
                <NewCommentAttachments errorMsg={newCommentContent.attachmentError} />
            </NewComment>
        );
    }

    function handleContentChange(value: string) {
        setNewCommentContent((p) => {
            return {
                ...p,
                error: "",
                value: value,
            };
        });
    }

    async function handleNewCommentSave() {
        const isValid = validateNewConsultation();
        if (!isValid) {
            return true;
        }
        const newConsultation = getNewConsultationBody();
        const createNewConsultationResponse = await consultationsService.create({ comment: newConsultation, attachments: commentAttachments });
        if (createNewConsultationResponse.errorMsg) {
            showAlert(createNewConsultationResponse.errorMsg, "error");
            return true;
        } else {
            await getUserConsulteeComments();
            setNewCommentDefaultValues();
            return false;
        }
    }

    function validateNewConsultation() {
        if (!newCommentContent.value) {
            setNewCommentContent((prev) => {
                return { ...prev, error: "Enter your consultation" };
            });
            return false;
        } else if (!consulteeToken.consultationId) {
            throw Error("validateNewConsultation - No consultation record found.");
        }
        const attachmentsValidationErrorMsg = validatePublicCommentAttachments(commentAttachments);
        if (attachmentsValidationErrorMsg) {
            setNewCommentContent((prev) => {
                return { ...prev, attachmentError: attachmentsValidationErrorMsg };
            });
            return false;
        }
        return true;
    }

    function getNewConsultationBody(): IConsulteeCommentToSave {
        return {
            consultationId: consulteeToken.consultationId,
            responseContent: newCommentContent.value,
            opinion: commentOpinion,
            remainAnonymous: remainAnonymous === "no" ? 0 : 1,
            paId: paId,
        } as IConsulteeCommentToSave;
    }

    function handleRemainAnonymousOnChange(event: React.ChangeEvent<HTMLInputElement>) {
        setRemainAnonymous(event.target.value);
    }

    function getUserConsultationCommentCards(): ICommentCard[] {
        return userComments?.comments?.map((comment, index) => {
            const commentCard: ICommentCard = {
                cardContent: comment.responseContent,
                cardTitle: `${comment.consulteeId_relatedRecord?.name || "(none)"} `,
                commentKey: `${comment.id}-${comment.createdTime}`,
                subheader: `${DateFormatter.formatToDateTime(comment.createdTime)} ${comment.opinion || ""}`,
                attachments: {
                    commentId: comment.id,
                    entityName: PublicPortalEntities.P_CONSULTEE_COMMENT,
                    attachmentsCounter: userComments.attachments[index]?.length,
                },
            };
            return commentCard;
        });
    }

    function getConsultationCommentCards(): ICommentCard[] {
        return publicComments.comments?.map((comment, index) => {
            const commentCard: ICommentCard = {
                cardContent: comment.responseDetailsToPublish,
                cardTitle: `${comment.consulteeId_relatedRecord?.name || "(none)"} `,
                commentKey: `${comment.id}-${comment.createdTime}`,
                subheader: `${DateFormatter.formatToDateTime(comment.createdTime)} ${comment.opinion || ""}`,
                attachments: {
                    commentId: comment.id,
                    entityName: PublicPortalEntities.PUBLIC_P_CONSULTEE_RESPONSE,
                    attachmentsCounter: publicComments.attachments[index]?.length,
                },
            };
            return commentCard;
        });
    }

    function setNewCommentDefaultValues() {
        setCommentOpinion("Neutral");
        setRemainAnonymous("no");
        setNewCommentContent({ value: "", error: "", attachmentError: "" });
    }

    async function getPublicConsulteeComments() {
        const response = await consultationsService.getPublicConsultations(paId || -1);
        setPublicComments(response);
    }

    async function getUserConsulteeComments() {
        if (!consulteeId) {
            return;
        }
        const response = await consultationsService.getUserConsultations({ paId: paId || -1 });
        setUserComments(response);
    }
};

export default PAConsultationsTabContent;
